import { Button, ButtonProps } from "@/components/ui/button";
import ImageSvg from "@/components/ui/image-svg";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

type ImageProps = {
  src: string;
  alt?: string;
};

type Feature = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  image: ImageProps;
};

type Tab = {
  value: string;
  trigger: string;
  content: Feature;
};

type Props = {
  heading: string;
  description: string;
  tabs: Tab[];
  defaultValue: string;
};

export type TransportSectionProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

export const TransportSection = (props: TransportSectionProps) => {
  const { heading, tabs, defaultValue } = {
    ...TransportSectionDefaults,
    ...props,
  };

  return (
    <section id="tipos-veiculos" className=" py-10 md:py-24 ">
      <div className="container">
        <div className="md:mx-auto mb-10 w-full text-center">
          <h2 className="text-3xl font-semibold lg:text-5xl text-n-green">
            {heading}
          </h2>
          {/* <p className="md:text-lg text-n-text">{description}</p> */}
        </div>
        <div className="grid auto-cols-fr grid-cols-1 gap-x-12 border border-border-primary lg:gap-x-0  rounded-[20px] bg-n-bg-2">
          <Tabs defaultValue={defaultValue}>
            <TabsList className="flex flex-col md:flex-row w-full pt-32 px-4 md:pt-16  md:px-10 gap-2 md:gap-3">
              {tabs.map((tab, index) => (
                <TabsTrigger
                  key={index}
                  value={tab.value}
                  className="flex w-full md:flex-1 md:min-w-[150px] md:h-[60px] h-[50px] text-n-text-primary border-2 border-n-primary items-center justify-center gap-2 whitespace-normal bg-transparent text-md md:text-xl font-semibold data-[state=active]:bg-n-primary data-[state=active]:text-n-secondary data-[state=active]:shadow-lg rounded-full hover:bg-n-bg-1 hover:shadow-sm duration-200"
                >
                  {tab.trigger}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((tab) => (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className="data-[state=active]:animate-tabs mt-[100px] md:mt-[0px]"
              >
                <FeatureCard tab={tab} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ tab }: { tab: Tab }) => {
  return (
    <div className="flex flex-col p-8 md:p-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
        <div className="flex flex-col gap-6">
          <h3 className="text-2xl md:text-4xl font-semibold leading-tight text-n-green">
            {tab.content.heading}
          </h3>
          <p className="text-n-text ">{tab.content.description}</p>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-4">
            {tab.content.buttons.map((button, index) => (
              <Link to="/fazer-cotacao" key={index}>
                <Button
                  {...button}
                  className="w-full sm:w-auto min-w-[200px] md:min-w-[150px]"
                >
                  {button.title}
                  <ArrowRight className=" h-6 w-6" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center order-first md:order-last mb-6 md:mb-0">
          <ImageSvg
            src={tab.content.image.src}
            className="w-full h-[200px] md:h-[300px] "
            alt={tab.content.image.alt}
          />
        </div>
      </div>
    </div>
  );
};

export const TransportSectionDefaults: Props = {
  heading: "Transportamos todos os tipos de veículos",
  description: "Levar seu carro para outro estado nunca foi tão fácil",
  defaultValue: "tab-1",
  tabs: [
    {
      value: "tab-1",
      trigger: "Carros",
      content: {
        tagline: "",
        heading: "O seu carro, de qualquer lugar para o seu destino de desejo.",
        description:
          "Nossos clientes buscam agilidade e segurança no transporte de veículos. Conhecer suas expectativas nos ajuda a superar suas necessidades.",
        buttons: [
          {
            title: "Agendar meu transporte online",
            variant: "default",
            id: "btn-fazer-cotacao",
          },
        ],
        image: {
          src: "carro.svg",
          alt: "Transporte de carros",
        },
      },
    },
    {
      value: "tab-2",
      trigger: "Motos",
      content: {
        tagline: "",
        heading: "A sua moto, de qualquer lugar para o seu destino de desejo.",
        description:
          "Nossos clientes buscam agilidade e segurança no transporte de veículos. Conhecer suas expectativas nos ajuda a superar suas necessidades.",
        buttons: [
          {
            title: "Agendar meu transporte online",
            variant: "default",
            id: "btn-fazer-cotacao",
          },
        ],
        image: {
          src: "moto.svg",
          alt: "Transporte de motos",
        },
      },
    },
    {
      value: "tab-3",
      trigger: "Utilitários",
      content: {
        tagline: "",
        heading:
          "O seu veículo utilitário, de qualquer lugar para o seu destino de desejo.",
        description:
          "Nossos clientes buscam agilidade e segurança no transporte de veículos. Conhecer suas expectativas nos ajuda a superar suas necessidades.",
        buttons: [
          {
            title: "Agendar meu transporte online",
            variant: "default",
            id: "btn-fazer-cotacao",
          },
        ],
        image: {
          src: "utilitarios.svg",
          alt: "Transporte de utilitários",
        },
      },
    },
    {
      value: "tab-4",
      trigger: "Aquáticos",
      content: {
        tagline: "",
        heading:
          "O seu veículo aquático, de qualquer lugar para o seu destino de desejo.",
        description:
          "Nossos clientes buscam agilidade e segurança no transporte de veículos. Conhecer suas expectativas nos ajuda a superar suas necessidades.",
        buttons: [
          {
            title: "Agendar meu transporte online",
            variant: "default",
            id: "btn-fazer-cotacao",
          },
        ],
        image: {
          src: "barco.svg",
          alt: "Transporte de veículos aquáticos",
        },
      },
    },
    {
      value: "tab-5",
      trigger: "Especiais",
      content: {
        tagline: "",
        heading:
          "O seu veículo especial, de qualquer lugar para o seu destino de desejo.",
        description:
          "Nossos clientes buscam agilidade e segurança no transporte de veículos. Conhecer suas expectativas nos ajuda a superar suas necessidades.",
        buttons: [
          {
            title: "Agendar meu transporte online",
            variant: "default",
            id: "btn-fazer-cotacao",
          },
        ],
        image: {
          src: "veiculos-especiais.svg",
          alt: "Transporte de veículos especiais",
        },
      },
    },
  ],
};
